<template>
  <!-- Süsteem -> Seaded -> Objekt uues kujunduses: one card per address with
       its objects side by side, the part of the address that differs marked,
       what kind of match it is in words, and the merge as a choice of which
       one stays — or "Pole duplikaat", which the scan then remembers. -->
  <div v-if="plate" class="obj-plate obj-list">
    <div class="obj-head">
      <div class="obj-head__text">
        <span class="obj-kicker obj-mid">OBJEKTID › OTSI DUPLIKAATE</span>
        <div class="obj-title"><h1 class="obj-wide">Võimalikud duplikaadid</h1></div>
        <span class="obj-lead">
          {{ loading && !objects.length ? 'Otsin…' : groups.length + ' aadressi, millel on kaks või rohkem objekti.' }}
          <template v-if="isAdmin"> Mida otsing ei leia, saab ühendada käsitsi.</template>
        </span>
      </div>
      <button v-if="isAdmin" type="button" class="obj-btn obj-btn--outline" @click="openManual()">
        <v-icon size="18">mdi-call-merge</v-icon>Ühenda käsitsi
      </button>
    </div>
    <v-alert v-if="capped" type="warning" variant="tonal" density="compact">
      Kuvatakse ainult esimesed {{ scanLimit }} objekti. Sellest piirist kaugemal olevaid duplikaate ei tuvastata.
    </v-alert>

    <!-- Ühenda käsitsi: what the scan does not group — another spelling, a
         typo, a building and its flat — picked by hand, two or more. -->
    <section v-if="manual.open" class="obj-card obj-dup obj-dup--manual">
      <div class="obj-dup__head">
        <h2 class="obj-wide">Ühenda käsitsi</h2>
        <span class="obj-lsub">Vali kaks või rohkem objekti, mis on tegelikult üks koht.</span>
        <span class="flex-grow-1" />
        <button type="button" class="obj-dup__x" aria-label="Sulge" @click="closeManual()">×</button>
      </div>
      <div class="obj-manual__search">
        <span class="obj-cap">OTSI OBJEKT — AADRESS VÕI POSTIINDEKS</span>
        <label class="obj-input" style="width: 100%">
          <v-icon size="17">mdi-magnify</v-icon>
          <input ref="manualInput" v-model="manual.query" type="search" placeholder="nt Pärna 41" aria-label="Otsi objekt">
          <span v-if="manual.searching" class="obj-lsub">otsin…</span>
          <span v-else-if="manual.query && manual.query.length > 1" class="obj-lsub">{{ manual.results.length }} objekti</span>
        </label>
      </div>
      <div v-if="manual.results.length" class="obj-manual__results">
        <div v-for="result in manual.results" :key="result.id" class="obj-manual__row">
          <b class="obj-ell">{{ $address(result.fullAddress) || result.address }}</b>
          <span><span class="obj-pill obj-pill--sm" :class="result.type === objectTypes.CONTRACT ? 'obj-pill--info' : 'obj-pill--grey'">{{ result.type === objectTypes.CONTRACT ? 'Lepinguline' : 'Tavaline' }}</span></span>
          <span class="obj-ell obj-lsub">{{ result.client && result.client.fullName || '—' }}</span>
          <button type="button" class="obj-manual__add" :aria-pressed="manual.ids.includes(result.id) ? 'true' : 'false'" @click="togglePick(result.id)">
            {{ manual.ids.includes(result.id) ? 'Valitud' : '+ Vali' }}
          </button>
        </div>
      </div>
    </section>
    <duplicate-group-card
      v-if="manual.open && manualGroup && (manualGroup.items.length || manualGroup.done)"
      :group="manualGroup"
      mode="manual"
      :admin="isAdmin"
      :keep-id="keeperOf(manualGroup)"
      :suggested-id="suggested(manualGroup)"
      :summary="manualGroup.items.length < 2 ? 'Vali otsingust veel vähemalt üks objekt.' : summaryOf(manualGroup)"
      :busy="busy === manualGroup.key"
      @keep="id => keep[manualGroup.key] = id"
      @remove="togglePick"
      @merge="confirmGroup = manualGroup"
    />

    <div class="obj-filters">
      <label class="obj-input" style="width: 260px">
        <v-icon size="17">mdi-magnify</v-icon>
        <input v-model="search.address" type="search" placeholder="Aadress" aria-label="Aadress">
      </label>
      <button v-for="chip in plateChips" :key="chip.key" type="button" class="obj-chip"
        :class="{ 'obj-chip--late': chip.warn, 'obj-chip--hidden': chip.key === 'hidden' }"
        :aria-pressed="kind === chip.key ? 'true' : 'false'" @click="pickKind(chip.key)">
        {{ chip.label }}<span class="obj-chip__n">{{ chip.n }}</span>
      </button>
    </div>

    <template v-if="kind === 'hidden'">
      <div v-if="!hidden.loading && !hiddenGroups.length" class="obj-card obj-empty">Peidetud rühmi pole.</div>
      <duplicate-group-card
        v-for="group in hiddenGroups"
        :key="group.key"
        :group="group"
        mode="hidden"
        :admin="isAdmin"
        :keep-id="keeperOf(group)"
        :suggested-id="suggested(group)"
        :summary="'Peidetud rühm ei tule otsingusse tagasi. Taasta toob selle tavalisse nimekirja. ' + summaryOf(group)"
        :busy="busy === group.key"
        @keep="id => keep[group.key] = id"
        @restore="restore(group)"
        @merge="confirmGroup = group"
      />
    </template>
    <template v-else>
      <div v-if="!loading && !shownGroups.length" class="obj-card obj-empty">Duplikaate ei leitud.</div>
      <duplicate-group-card
        v-for="group in shownGroups.slice(0, shownCount)"
        :key="group.key"
        :group="group"
        :admin="isAdmin"
        :keep-id="keeperOf(group)"
        :suggested-id="suggested(group)"
        :summary="summaryOf(group)"
        :busy="busy === group.key"
        @keep="id => keep[group.key] = id"
        @dismiss="dismiss(group)"
        @merge="confirmGroup = group"
      />
      <div v-if="shownGroups.length > shownCount" class="d-flex justify-center">
        <button type="button" class="obj-btn obj-btn--outline" @click="shownCount += 30">Näita veel ({{ shownGroups.length - shownCount }})</button>
      </div>
    </template>
    <confirm
      :visible="!!confirmGroup"
      title="Ühenda objektid?"
      action="Ühenda"
      :danger="false"
      @cancel="confirmGroup = null"
      @confirm="mergeGroup(confirmGroup)"
    >
      {{ confirmGroup ? summaryOf(confirmGroup) : '' }}
      <v-alert v-if="confirmGroup && (!confirmGroup.sameAddress || !confirmGroup.sameClient)" type="warning" class="mt-3 mb-0" variant="tonal" density="compact">
        {{ !confirmGroup.sameAddress ? 'Aadressid erinevad' : 'Kliendid erinevad' }} — kontrolli, et see on tõesti üks koht.
      </v-alert>
      <v-alert type="warning" class="mt-3 mb-0" variant="tonal" density="compact">Kustutatud objekt ei ole taastatav.</v-alert>
    </confirm>
  </div>
  <loading-content v-else :loading="loading">
    <heading>
      Objektid
      <v-btn v-if="isAdmin && selected.length === 2" color="orange-darken-1" class="mr-2" @click="openMerge">Ühenda valitud</v-btn>
      <v-btn @click="openSearch" color="primary">Duplikaatide otsing</v-btn>
    </heading>

    <v-alert v-if="capped" type="warning" class="mb-5" variant="tonal" density="compact">
      Kuvatakse ainult esimesed {{ scanLimit }} objekti. Sellest piirist kaugemal olevaid duplikaate ei tuvastata.
    </v-alert>

    <card class="mb-5" v-if="searchVisible">
      <h3>Duplikaatide otsing</h3>
      <v-row>
        <v-col>
          <v-text-field
            v-model="search.address"
            label="Aadress"
            clearable
            hide-details
            variant="outlined"
            density="compact"
          />
        </v-col>
      </v-row>
    </card>

    <v-data-table
      class="table"
      :headers="headers"
      :loading="loading"
      :items="objects"
      v-model:items-per-page="itemsPerPage"
      :items-per-page-options="itemsPerPageOptions"
      v-model="selected"
      :show-select="isAdmin"
      item-value="id"
      return-object
      density="compact"
    >
      <template v-slot:[`item.fullAddress`]="{ item }">
        <router-link :to="{ name: 'object/view/info', params: { objectId: item.id } }" :class="{ 'text-red': item.problematic }">
          {{ $address(item.fullAddress) }}
        </router-link>
      </template>
      <template v-slot:[`item.calendarCount`]="{ item }">
        <span>{{ item.calendarCount }}</span>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-btn v-if="canEdit('object', item)" color="primary" @click="$router.push({ name: 'object/edit', params: { objectId: item.id } })" size="x-small">
          Muuda
        </v-btn>
      </template>
    </v-data-table>

    <!-- Merge dialog -->
    <v-dialog v-model="mergeDialog" max-width="560" persistent>
      <v-card v-if="selected.length === 2">
        <v-card-title class="text-h5">Objektide ühendamine</v-card-title>
        <v-card-text>
          <p class="mb-4">Valige, milline objekt säilitatakse. Teine objekt kustutatakse ja selle kalendri- ning märkusekirjed teisaldatakse säilitatavale.</p>
          <v-radio-group v-model="keepId">
            <v-radio
              v-for="obj in selected"
              :key="obj.id"
              :value="obj.id"
            >
              <template v-slot:label>
                <div>
                  <strong>{{ $address(obj.fullAddress) || obj.address }}</strong>
                  <span class="ml-2 text-grey">(id: {{ obj.id }})</span>
                  <v-chip class="ml-2" color="blue" size="x-small" variant="flat">{{ obj.calendarCount }} kalendrikirjet</v-chip>
                  <span v-if="obj.client" class="ml-2 text-grey-darken-1">{{ obj.client.fullName }}</span>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
          <v-alert type="warning" class="mt-2" variant="tonal" density="compact">
            Kustutatud objekt ei ole taastatav. Kliendi andmed jäävad muutmata.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="mergeDialog = false" variant="text">Tühista</v-btn>
          <v-btn color="orange-darken-1" :loading="merging" @click="confirmMerge">Ühenda</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </loading-content>
</template>

<script>
import api from '@/api'
import helpersMixin from '@/plugins/helpersMixin'
import { mapActions } from 'vuex'
import debounce from 'lodash/debounce'
import dayjs from 'dayjs'
import { mapState } from 'vuex'
import loadArchivo from '@/plugins/archivo'
import '@/styles/object-plate.css'
import DuplicateGroupCard from '@/components/DuplicateGroupCard.vue'

export default {
  mixins: [ helpersMixin ],
  components: { DuplicateGroupCard },
  data () {
    return {
      loading: false,
      merging: false,
      objects: [],
      capped: false,
      scanLimit: 0,
      selected: [],
      searchVisible: false,
      mergeDialog: false,
      keepId: null,
      search: { address: '' },
      // 30 to start with: Vuetify 2 opened on the first of the options when its
      // own default (10) was not one of them, and Vuetify 3 keeps the 10.
      itemsPerPage: 30,
      itemsPerPageOptions: [30, 50, 100],
      // The new look: which kind of group is shown, who stays per group, the
      // groups already answered, and how many cards are drawn so far.
      kind: 'all',
      keep: {},
      answered: {},
      busy: null,
      confirmGroup: null,
      shownCount: 30,
      // Ühenda käsitsi: the search, its results and what was picked (counted
      // by GET /object/merge-preview).
      manual: { open: false, query: '', results: [], searching: false, ids: [], items: [] },
      // Peidetud: the groups marked "Pole duplikaat".
      hidden: { loading: false, groups: [], count: 0 },
      manualAsk: 0
    }
  },
  computed: {
    ...mapState(['features']),
    plate () {
      return !!(this.features && this.features.objectPlate)
    },
    plateAdmin () {
      return this.plate && this.isAdmin
    },
    // The scan's rows, back in the groups it found them in.
    groups () {
      const byKey = new Map()
      for (const object of this.objects) {
        const key = object.group || object.id
        if (!byKey.has(key)) byKey.set(key, [])
        byKey.get(key).push(object)
      }
      return [...byKey.entries()].map(([key, items]) => this.buildGroup(key, items))
    },
    hiddenGroups () {
      return this.hidden.groups.map(group => {
        const key = 'hidden-' + group.ids.join('-')
        const when = group.hiddenAt ? dayjs(group.hiddenAt).format('DD.MM.YYYY') : ''
        const label = ['Peidetud', [group.hiddenBy && 'märkis ' + group.hiddenBy, when].filter(Boolean).join(' ')].filter(Boolean).join(' · ')
        return this.buildGroup(key, group.objects, [{ label, cls: 'obj-pill--grey' }])
      })
    },
    manualGroup () {
      const group = this.buildGroup('manual', this.manual.items, [], 'Valitud objektid')
      return group
    },
    shownGroups () {
      return this.groups.filter(group => group.done || this.kind === 'all' ||
        (this.kind === 'address' && group.sameAddress) ||
        (this.kind === 'client' && group.sameClient) ||
        (this.kind === 'check' && !group.sameAddress))
    },
    plateChips () {
      const open = this.groups.filter(group => !group.done)
      const chips = [
        { key: 'all', label: 'Kõik', n: open.length },
        { key: 'address', label: 'Sama aadress', n: open.filter(g => g.sameAddress).length },
        { key: 'client', label: 'Sama klient', n: open.filter(g => g.sameClient).length },
        { key: 'check', label: 'Kontrolli: aadress erineb', n: open.filter(g => !g.sameAddress).length, warn: true }
      ]
      if (this.isAdmin) chips.push({ key: 'hidden', label: 'Peidetud', n: this.hidden.groups.filter(g => !this.answered['hidden-' + g.ids.join('-')]).length })
      return chips
    },
    headers () {
      return [{
        title: 'Aadress',
        align: 'start',
        value: 'fullAddress',
        sortable: false
      }, {
        title: 'Klient',
        align: 'start',
        value: 'client.fullName',
        sortable: false
      }, {
        title: 'Tehnik',
        align: 'start',
        value: 'technic.fullName',
        sortable: false
      }, {
        title: 'Kalender',
        align: 'center',
        value: 'calendarCount',
        sortable: false
      }, {
        title: '',
        value: 'actions',
        sortable: false,
        align: 'end'
      }]
    }
  },
  watch: {
    // The switch and the roles may arrive after mount; the Peidetud chip says
    // how many there are before it is opened.
    plateAdmin: {
      handler (on) {
        if (on) {
          loadArchivo()
          this.loadHidden()
        } else if (this.plate) {
          loadArchivo()
        }
      },
      immediate: true
    },
    'manual.query': debounce(async function (query) {
      if (!query || query.trim().length < 2) {
        this.manual.results = []
        return
      }
      this.manual.searching = true
      try {
        const { objects } = await api.searchObjects({ query: query.trim() })
        if (query === this.manual.query) this.manual.results = objects.slice(0, 15)
      } catch (err) {
        this.manual.results = []
      } finally {
        this.manual.searching = false
      }
    }, 350),
    'search.address': debounce(function () {
      this.refresh()
    }, 400)
  },
  methods: {
    ...mapActions(['showNotification']),
    day (date) {
      return date && dayjs(date).isValid() ? dayjs(date).format('DD.MM.YYYY') : '—'
    },
    // One card's worth: what kind of match it is in words, and each address
    // split round the part that differs from the other one.
    buildGroup (key, items, extraTags = [], title = null) {
      const tags = [...extraTags]
      let sameAddress = true
      let sameClient = true
      if (items.length > 1) {
        sameAddress = new Set(items.map(o => o.compareKey)).size === 1
        const clients = new Set(items.map(o => (o.client && o.client.id) || null))
        sameClient = clients.size === 1 && !clients.has(null)
        if (sameAddress) {
          tags.push(new Set(items.map(o => o.address)).size > 1
            ? { label: 'Sama aadress, erinev kirjapilt', cls: 'obj-pill--ok' }
            : { label: 'Sama aadress', cls: 'obj-pill--ok' })
        } else {
          tags.push({ label: 'Aadress erineb — kontrolli, et see on üks koht', cls: 'obj-pill--late' })
        }
        tags.push(sameClient ? { label: 'Sama klient', cls: 'obj-pill--grey' } : { label: 'Erinev klient', cls: 'obj-pill--info' })
      }
      const reference = (items[0] && items[0].address) || ''
      const second = (items[1] && items[1].address) || ''
      return {
        key,
        sameAddress,
        sameClient,
        title: title || (items[0] && (this.$address(items[0].fullAddress) || reference)) || '',
        tags,
        done: this.answered[key] && this.answered[key].text,
        doneId: this.answered[key] && this.answered[key].id,
        items: items.map((o, i) => ({ ...o, parts: this.diffParts(o.address || '', i === 0 ? second : reference) }))
      }
    },
    pickKind (key) {
      this.kind = key
      if (key === 'hidden') this.loadHidden()
    },
    async loadHidden () {
      if (!this.isAdmin) return
      this.hidden.loading = true
      try {
        const { groups } = await api.getHiddenDuplicates()
        this.hidden.groups = groups
      } catch (err) {
        this.hidden.groups = []
      } finally {
        this.hidden.loading = false
      }
    },
    async restore (group) {
      this.busy = group.key
      try {
        await api.restoreDuplicate(group.items.map(o => o.id))
        this.answered[group.key] = { id: null, text: 'Taastatud: rühm on tagasi tavalises nimekirjas.' }
        this.refresh()
      } catch (err) {
        this.showNotification({ text: err.message || 'Taastamine ebaõnnestus', color: 'red' })
      } finally {
        this.busy = null
      }
    },
    openManual () {
      this.manual.open = true
      delete this.answered.manual
      this.$nextTick(() => this.$refs.manualInput && this.$refs.manualInput.focus())
    },
    closeManual () {
      this.manual = { open: false, query: '', results: [], searching: false, ids: [], items: [] }
      delete this.answered.manual
    },
    async togglePick (id) {
      delete this.answered.manual
      const ids = this.manual.ids.includes(id) ? this.manual.ids.filter(x => x !== id) : [...this.manual.ids, id]
      this.manual.ids = ids
      // Each pick asks again; only the newest answer is used.
      const ask = ++this.manualAsk
      if (!ids.length) {
        this.manual.items = []
        return
      }
      try {
        const { objects } = await api.getMergePreview(ids)
        if (ask === this.manualAsk) this.manual.items = objects
      } catch (err) {
        this.showNotification({ text: err.message || 'Objekti andmeid ei saanud', color: 'red' })
      }
    },
    // The address split round the part that differs from the other one in
    // its group: common start, the difference, common end.
    diffParts (address, other) {
      if (!other || address === other) return [address, '', '']
      let start = 0
      while (start < address.length && start < other.length && address[start] === other[start]) start++
      let end = 0
      while (end < address.length - start && end < other.length - start &&
        address[address.length - 1 - end] === other[other.length - 1 - end]) end++
      return [address.slice(0, start), address.slice(start, address.length - end), address.slice(address.length - end)]
    },
    history (item) {
      return item.calendarCount + item.noteCount + item.aktCount + item.deviceCount
    },
    // What stays unless someone picks otherwise: a contract object, then the
    // one with the most behind it, then the older one.
    suggested (group) {
      if (!group.items.length) return null
      return group.items.slice().sort((a, b) =>
        ((b.type === this.objectTypes.CONTRACT) - (a.type === this.objectTypes.CONTRACT)) ||
        (this.history(b) - this.history(a)) || (a.id - b.id))[0].id
    },
    // The office's pick while it is still in the group, else the suggestion.
    keeperOf (group) {
      const picked = this.keep[group.key]
      return picked && group.items.some(o => o.id === picked) ? picked : this.suggested(group)
    },
    summaryOf (group) {
      const keepId = this.keeperOf(group)
      const keeper = group.items.find(o => o.id === keepId)
      const others = group.items.filter(o => o.id !== keepId)
      const sum = field => others.reduce((n, o) => n + (o[field] || 0), 0)
      const moving = [
        [sum('calendarCount'), 'töökäsku'], [sum('noteCount'), 'märkust'],
        [sum('aktCount'), 'akti'], [sum('deviceCount'), 'seadet']
      ].filter(([n]) => n).map(([n, word]) => n + ' ' + word)
      return 'Jääb ' + keeper.address + ' (' + (keeper.type === this.objectTypes.CONTRACT ? 'lepinguline' : 'tavaline') +
        (keeper.client && keeper.client.fullName ? ', ' + keeper.client.fullName : '') + '). ' +
        (others.length === 1 ? 'Teine kustutatakse' : others.length + ' teist kustutatakse') +
        (moving.length ? '; üle liigub ' + moving.join(', ') : '') + '. Klientide andmed jäävad muutmata.'
    },
    // One merge per object that goes: the server merges two at a time.
    async mergeGroup (group) {
      this.confirmGroup = null
      if (!group) return
      const keepId = this.keeperOf(group)
      const keeper = group.items.find(o => o.id === keepId)
      this.busy = group.key
      let moved = 0
      try {
        for (const other of group.items.filter(o => o.id !== keepId)) {
          const result = await api.mergeObjects({ keepId, deleteId: other.id })
          moved += result.moved || 0
        }
        this.answered[group.key] = { id: keepId, text: 'Ühendatud: alles jäi ' + keeper.address + ', ' + moved + ' töökäsku liikus sinna.' }
        if (group.key === 'manual') {
          this.manual.ids = []
          this.manual.items = []
          this.manual.results = this.manual.results.filter(r => r.id === keepId || !group.items.some(o => o.id === r.id))
        }
        this.showNotification({ text: 'Ühendamine õnnestus. ' + moved + ' kalendrikirjet teisaldati.', color: 'success' })
      } catch (err) {
        this.showNotification({ text: err.message || 'Ühendamine ebaõnnestus', color: 'red' })
        this.refresh()
      } finally {
        this.busy = null
      }
    },
    async dismiss (group) {
      this.busy = group.key
      try {
        await api.markNotDuplicate(group.items.map(o => o.id))
        this.answered[group.key] = { id: null, text: 'Märgitud: need on eri kohad. Seda rühma enam ei pakuta.' }
      } catch (err) {
        this.showNotification({ text: err.message || 'Salvestamine ebaõnnestus', color: 'red' })
      } finally {
        this.busy = null
      }
    },
    openSearch () {
      this.searchVisible = true
      this.refresh()
    },
    openMerge () {
      this.keepId = this.selected[0].id
      this.mergeDialog = true
    },
    async confirmMerge () {
      const deleteId = this.selected.find(o => o.id !== this.keepId).id
      this.merging = true
      try {
        const { moved } = await api.mergeObjects({ keepId: this.keepId, deleteId })
        this.mergeDialog = false
        this.selected = []
        this.showNotification({ text: `Ühendamine õnnestus. ${moved} kalendrikirjet teisaldati.`, color: 'success' })
      } catch (err) {
        this.showNotification({ text: err.message || 'Ühendamine ebaõnnestus', color: 'red' })
      } finally {
        this.merging = false
      }
      this.refresh()
    },
    async refresh () {
      this.loading = true
      try {
        const { objects, capped, scanLimit } = await api.getDuplicateObjects({ address: this.search.address || undefined })
        this.objects = objects
        this.answered = {}
        this.capped = capped || false
        this.scanLimit = scanLimit || 0
      } catch (err) {
        this.showNotification({ text: err.message || 'Viga duplikaatide laadimisel', color: 'red' })
      } finally {
        this.loading = false
      }
    }
  },
  mounted () {
    this.refresh()
  }
}
</script>
